import { useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, RotateCcw, Download } from "lucide-react";
import { apiFetch } from "@/lib/api";

const ADMIN_KEY_STORAGE = "mk_admin_key";

function Shell({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "3rem 1.5rem 4rem",
        background: "radial-gradient(circle at 50% 20%, #14102A 0%, #03030A 70%)",
        color: "#F4F6F0",
        fontFamily: "Outfit, system-ui, sans-serif",
        position: "relative",
      }}
    >
      <div className="mandala-watermark" />
      <div className="grain-overlay" />
      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 3 }}>
        <p className="subtitle-caps" style={{ marginBottom: "2rem" }}>Mind Kundli — Admin</p>
        {children}
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "0.85rem 1rem",
  borderRadius: 10,
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(212, 175, 55, 0.25)",
  color: "#F4F6F0",
  fontFamily: "Outfit, sans-serif",
  fontSize: "0.95rem",
  outline: "none",
};

const STATUS_COLORS = {
  not_started: "#A0A0B0",
  generating: "#D4AF37",
  needs_review: "#E0115F",
  generation_failed: "#E0115F",
  approved: "#50C878",
  sent: "#50C878",
};

function statusLabel(status) {
  const labels = {
    not_started: "Not started",
    generating: "Generating",
    needs_review: "Needs review",
    generation_failed: "Failed",
    approved: "Approved",
    sent: "Sent",
  };
  return labels[status] || status;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={active ? "cta-button" : "ghost-button"}
      style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
    >
      {children}
    </button>
  );
}

export default function Admin() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [keyInput, setKeyInput] = useState("");
  const [unlockError, setUnlockError] = useState("");

  // Which tab is showing. Loading is lazy per tab the first time it's opened,
  // then cached in state so switching back and forth doesn't re-fetch.
  const [tab, setTab] = useState("review");

  // Needs Review tab state
  const [reports, setReports] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState("");
  const [actioningId, setActioningId] = useState(null);

  // All Customers tab state
  const [customers, setCustomers] = useState(null);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState("");

  const handleAuthError = (err) => {
    if (err.status === 401 || err.status === 403) {
      setUnlockError("That admin key was rejected.");
      sessionStorage.removeItem(ADMIN_KEY_STORAGE);
      setAdminKey("");
      return true;
    }
    return false;
  };

  const loadReports = async (key) => {
    setReportsLoading(true);
    setReportsError("");
    try {
      const data = await apiFetch("/admin/reports/needs_review", { adminKey: key });
      setReports(data.reports || []);
    } catch (err) {
      if (!handleAuthError(err)) setReportsError(err.message || "Couldn't load the review queue.");
    } finally {
      setReportsLoading(false);
    }
  };

  const loadCustomers = async (key) => {
    setCustomersLoading(true);
    setCustomersError("");
    try {
      const data = await apiFetch("/admin/customers", { adminKey: key });
      setCustomers(data.customers || []);
    } catch (err) {
      if (!handleAuthError(err)) setCustomersError(err.message || "Couldn't load customers.");
    } finally {
      setCustomersLoading(false);
    }
  };

  // Load the active tab's data once we have a key, and whenever the tab
  // changes to one that hasn't been loaded yet.
  useEffect(() => {
    if (!adminKey) return;
    if (tab === "review" && reports === null) loadReports(adminKey);
    if (tab === "customers" && customers === null) loadCustomers(adminKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKey, tab]);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    sessionStorage.setItem(ADMIN_KEY_STORAGE, keyInput.trim());
    setAdminKey(keyInput.trim());
  };

  const handleApprove = async (reportId) => {
    setActioningId(reportId);
    try {
      await apiFetch(`/admin/reports/${reportId}/approve`, { method: "POST", adminKey });
      await loadReports(adminKey);
    } catch (err) {
      setReportsError(err.message || "Approve failed.");
    } finally {
      setActioningId(null);
    }
  };

  const handleRegenerate = async (reportId) => {
    setActioningId(reportId);
    try {
      await apiFetch(`/admin/reports/${reportId}/regenerate`, { method: "POST", adminKey });
      await loadReports(adminKey);
    } catch (err) {
      setReportsError(err.message || "Regenerate failed.");
    } finally {
      setActioningId(null);
    }
  };

  const downloadCsv = () => {
    if (!customers || customers.length === 0) return;
    const headers = ["email", "name", "phone", "paid", "created_at", "assessment_completed", "report_status", "archetype", "overall_score"];
    const rows = customers.map((c) => headers.map((h) => JSON.stringify(c[h] ?? "")).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mind-kundli-customers.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!adminKey) {
    return (
      <Shell>
        <div className="glass-panel" style={{ padding: "2rem", borderRadius: 20, maxWidth: 420 }}>
          <h1 className="serif" style={{ fontSize: "1.5rem", marginBottom: "1.25rem", fontWeight: 500 }}>
            Enter admin key
          </h1>
          <form onSubmit={handleUnlock}>
            <input
              type="password"
              data-testid="admin-key-input"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              style={inputStyle}
              placeholder="ADMIN_API_KEY"
            />
            {unlockError && <p style={{ color: "#E0115F", fontSize: "0.85rem", marginTop: "0.75rem" }}>{unlockError}</p>}
            <button type="submit" className="cta-button" style={{ width: "100%", justifyContent: "center", marginTop: "1.25rem" }}>
              Unlock
            </button>
          </form>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <TabButton active={tab === "review"} onClick={() => setTab("review")}>
            Needs Review{reports && reports.length > 0 ? ` (${reports.length})` : ""}
          </TabButton>
          <TabButton active={tab === "customers"} onClick={() => setTab("customers")}>
            All Customers
          </TabButton>
        </div>

        {tab === "review" && (
          <button onClick={() => loadReports(adminKey)} className="ghost-button" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <RefreshCw size={14} /> Refresh
          </button>
        )}
        {tab === "customers" && (
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <button onClick={downloadCsv} disabled={!customers?.length} className="ghost-button" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <Download size={14} /> Export CSV
            </button>
            <button onClick={() => loadCustomers(adminKey)} className="ghost-button" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        )}
      </div>

      {tab === "review" && (
        <>
          {reportsError && <p style={{ color: "#E0115F", marginBottom: "1rem" }}>{reportsError}</p>}
          {reportsLoading && <p style={{ color: "#A0A0B0" }}>Loading…</p>}
          {reports && reports.length === 0 && !reportsLoading && (
            <p style={{ color: "#A0A0B0" }}>Nothing flagged right now — all clear.</p>
          )}
          {reports && reports.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              {reports.map((r) => (
                <div key={r.id} className="glass-panel" style={{ padding: "1.5rem", borderRadius: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
                    <span style={{ fontSize: "0.8rem", color: "#A0A0B0" }}>
                      Report {r.id.slice(0, 8)} · user {r.user_id.slice(0, 8)}
                    </span>
                    <span style={{ fontSize: "0.8rem", color: "#A0A0B0" }}>{r.updated_at}</span>
                  </div>
                  <p style={{ color: "#E0115F", fontSize: "0.9rem", marginBottom: "1rem" }}>
                    Flagged: {r.qa_reason || "No reason given"}
                  </p>
                  <details style={{ marginBottom: "1.25rem" }}>
                    <summary style={{ cursor: "pointer", color: "#D4AF37", fontSize: "0.85rem" }}>
                      View report text
                    </summary>
                    <div style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem", color: "#D8D8E2", marginTop: "0.75rem", lineHeight: 1.6 }}>
                      {r.report_text}
                    </div>
                  </details>
                  <div style={{ display: "flex", gap: "0.75rem" }}>
                    <button
                      onClick={() => handleApprove(r.id)}
                      disabled={actioningId === r.id}
                      className="cta-button"
                      style={{ opacity: actioningId === r.id ? 0.6 : 1 }}
                    >
                      <CheckCircle2 size={16} /> Approve & send
                    </button>
                    <button
                      onClick={() => handleRegenerate(r.id)}
                      disabled={actioningId === r.id}
                      className="ghost-button"
                      style={{ opacity: actioningId === r.id ? 0.6 : 1 }}
                    >
                      <RotateCcw size={14} /> Regenerate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "customers" && (
        <>
          {customersError && <p style={{ color: "#E0115F", marginBottom: "1rem" }}>{customersError}</p>}
          {customersLoading && <p style={{ color: "#A0A0B0" }}>Loading…</p>}
          {customers && customers.length === 0 && !customersLoading && (
            <p style={{ color: "#A0A0B0" }}>No customers yet.</p>
          )}
          {customers && customers.length > 0 && (
            <div className="glass-panel" style={{ borderRadius: 16, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr style={{ textAlign: "left", borderBottom: "1px solid rgba(212,175,55,0.25)" }}>
                    {["Email", "Name", "Paid", "Paid At", "Assessment", "Report Status", "Archetype", "Score"].map((h) => (
                      <th key={h} style={{ padding: "0.85rem 1rem", color: "#D4AF37", fontWeight: 500, whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <td style={{ padding: "0.75rem 1rem" }}>{c.email}</td>
                      <td style={{ padding: "0.75rem 1rem" }}>{c.name || "—"}</td>
                      <td style={{ padding: "0.75rem 1rem", color: c.paid ? "#50C878" : "#E0115F" }}>{c.paid ? "Yes" : "No"}</td>
                      <td style={{ padding: "0.75rem 1rem", color: "#A0A0B0", whiteSpace: "nowrap" }}>{formatDate(c.created_at)}</td>
                      <td style={{ padding: "0.75rem 1rem" }}>{c.assessment_completed ? "Done" : "—"}</td>
                      <td style={{ padding: "0.75rem 1rem", color: STATUS_COLORS[c.report_status] || "#A0A0B0" }}>
                        {statusLabel(c.report_status)}
                      </td>
                      <td style={{ padding: "0.75rem 1rem", color: "#A0A0B0" }}>{c.archetype || "—"}</td>
                      <td style={{ padding: "0.75rem 1rem", color: "#A0A0B0" }}>{c.overall_score != null ? `${c.overall_score}/100` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Shell>
  );
}
